import React from 'react';
import { useCustomEventListener } from 'react-custom-events';
import { useLocation, useNavigate } from 'react-router-dom';

import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import { Box, Button, Paper, Popover, Typography } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ZoneItem {
    id: string;
    name: string;
    description: string;
    parent: string;
    order?: number;
    virtual?: true;
    virtualSource?: { zoneId: string; type: string };
}

interface ZoneTreeNode extends ZoneItem {
    children: ZoneTreeNode[];
}

// ─── URL helpers ─────────────────────────────────────────────────────────────

const ZONE_PAGE_BASES = ['/', '/light-channels', '/spectrum-mixes', '/projects'];

const ZONE_ID_PATTERN = new RegExp('/zone/([^/]+)');

function extractZoneId(pathname: string): string | null {
    const match = ZONE_ID_PATTERN.exec(pathname);
    return match ? match[1] : null;
}

function getPageBase(pathname: string): string {
    const withoutZone = pathname.replace(/\/zone\/[^/]+/, '');
    const withoutPageType = withoutZone.replace(/\/(new|edit|view)(\/[^/]+)?$/, '');
    return withoutPageType || '/';
}

function supportsZoneRoute(pathname: string): boolean {
    const base = getPageBase(pathname);
    return ZONE_PAGE_BASES.some((p) => base === p || (p !== '/' && base.startsWith(p + '/')));
}

function buildZoneUrl(pathname: string, zoneId: string): string {
    const base = getPageBase(pathname);
    return base === '/' ? `/zone/${zoneId}` : `${base}/zone/${zoneId}`;
}

// ─── Tree helpers ─────────────────────────────────────────────────────────────

function buildZoneTree(zones: ZoneItem[]): ZoneTreeNode | null {
    const rootItem: ZoneItem = { id: 'root', name: 'Root', description: '', parent: '', order: 0 };
    const all: ZoneItem[] = [rootItem, ...zones];

    const map: Record<string, ZoneTreeNode> = {};
    all.forEach((z) => {
        map[z.id] = { ...z, order: z.order ?? 0, children: [] };
    });

    const roots: ZoneTreeNode[] = [];
    all.forEach((z) => {
        if (z.parent && map[z.parent]) {
            map[z.parent].children.push(map[z.id]);
        } else if (z.id === 'root') {
            roots.push(map[z.id]);
        } else {
            map.root?.children.push(map[z.id]);
        }
    });

    function sortChildren(node: ZoneTreeNode): void {
        node.children.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        node.children.forEach(sortChildren);
    }

    const rootNode = roots[0] ?? null;
    if (rootNode) {
        sortChildren(rootNode);
    }
    return rootNode;
}

function getLeafIds(zones: ZoneItem[]): Set<string> {
    const parentIds = new Set(zones.map((z) => z.parent));
    return new Set(zones.filter((z) => !parentIds.has(z.id)).map((z) => z.id));
}

// ─── SelectZonePopover ────────────────────────────────────────────────────────

interface SelectZonePopoverProps {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    onSelect: (zoneId: string, mirrorZoneId?: string) => void;
    zones: ZoneItem[];
    currentZoneId?: string | null;
}

function SelectZonePopover({ anchorEl, onClose, onSelect, zones, currentZoneId }: SelectZonePopoverProps) {
    const open = Boolean(anchorEl);
    const tree = React.useMemo(() => buildZoneTree(zones), [zones]);
    const leafIds = React.useMemo(() => getLeafIds(zones), [zones]);
    const [expanded, setExpanded] = React.useState<string[]>([]);

    React.useEffect(() => {
        if (open) {
            setExpanded(['root', ...zones.map((z) => z.id)]);
        }
    }, [open, zones]);

    function handleSelect(_event: React.SyntheticEvent, itemIds: string | string[] | null) {
        const id = Array.isArray(itemIds) ? itemIds[0] : itemIds;
        if (!id || !leafIds.has(id)) {
            return;
        }
        const selectedZone = zones.find((z) => z.id === id);
        if (selectedZone?.virtual && selectedZone.virtualSource) {
            onSelect(selectedZone.virtualSource.zoneId, id);
        } else {
            onSelect(id);
        }
        onClose();
    }

    function renderTree(node: ZoneTreeNode): React.ReactElement {
        const isRoot = node.id === 'root';
        const isLeaf = leafIds.has(node.id);
        const isCurrent = node.id === currentZoneId;

        return (
            <TreeItem
                key={node.id}
                itemId={node.id}
                label={
                    <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                        {node.virtual && <LinkIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />}
                        <Typography variant="body2" sx={{ color: isRoot || !isLeaf ? 'text.secondary' : 'text.primary' }}>
                            {node.name}
                        </Typography>
                        {isCurrent && (
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled' }}>
                                (current)
                            </Typography>
                        )}
                    </Box>
                }
            >
                {node.children.map((child) => renderTree(child))}
            </TreeItem>
        );
    }

    const hasLeaves = zones.some((z) => leafIds.has(z.id));

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
            <Box sx={{ p: 2, minWidth: 240, maxHeight: 'min(400px, 50vh)', overflowY: 'auto' }}>
                {!hasLeaves && <Typography color="text.secondary">No zones available. Add a zone first.</Typography>}
                {hasLeaves && tree && (
                    <SimpleTreeView
                        slots={{ collapseIcon: ExpandMoreIcon, expandIcon: ChevronRightIcon }}
                        expandedItems={expanded}
                        onExpandedItemsChange={(_event, itemIds) => setExpanded(itemIds)}
                        onSelectedItemsChange={handleSelect}
                    >
                        {renderTree(tree)}
                    </SimpleTreeView>
                )}
            </Box>
        </Popover>
    );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getActiveZoneLabel(zonesLoaded: boolean, zoneName: string | undefined): string {
    if (!zonesLoaded) {
        return '';
    }
    return zoneName ?? '';
}

// ─── ZoneSelector ─────────────────────────────────────────────────────────────

function ZoneSelector() {
    const {
        globalState: { userData, selectedGroupId, previousGroupId, dashboardGroupId },
        globalActions
    } = useGlobalStore();
    const navigate = useNavigate();
    const location = useLocation();
    const [zones, setZones] = React.useState<ZoneItem[]>([]);
    const [zonesLoaded, setZonesLoaded] = React.useState(false);
    const [selectZoneAnchor, setSelectZoneAnchor] = React.useState<HTMLElement | null>(null);
    const [selectedMirrorZoneId, setSelectedMirrorZoneId] = React.useState<string | null>(null);
    const selectZoneBtnRef = React.useRef<HTMLButtonElement>(null);

    const isDashboard = getPageBase(location.pathname) === '/';

    async function loadZones() {
        const data = await lediumApi.get<ZoneItem[]>('zones');
        if (data) {
            const filtered = data.filter((z) => z.id !== 'root');
            setZones(filtered);
            if (selectedGroupId !== null && !filtered.some((z) => z.id === selectedGroupId)) {
                globalActions.zones.setSelectedGroup(null);
            }
            if (dashboardGroupId !== null && !filtered.some((z) => z.id === dashboardGroupId)) {
                globalActions.zones.setDashboardGroup(null);
            }
        }
        setZonesLoaded(true);
    }

    React.useEffect(() => {
        if (userData.auth) {
            loadZones();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData.auth]);

    React.useEffect(() => {
        if (selectedMirrorZoneId !== null && !zones.some((z) => z.id === selectedMirrorZoneId)) {
            setSelectedMirrorZoneId(null);
        }
    }, [zones, selectedMirrorZoneId]);

    useCustomEventListener('zonesChanged', async (data: { selectZoneId?: string }) => {
        await loadZones();
        if (data?.selectZoneId) {
            navigateToZone(data.selectZoneId);
        }
    });

    // Sync store from URL when navigating to a zone-aware page with a zone segment
    React.useEffect(() => {
        if (!supportsZoneRoute(location.pathname)) {
            return;
        }
        const urlZoneId = extractZoneId(location.pathname);
        if (urlZoneId !== null && urlZoneId !== selectedGroupId) {
            globalActions.zones.setSelectedGroup(urlZoneId);
            globalActions.zones.setDashboardGroup(urlZoneId);
            setSelectedMirrorZoneId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    function navigateToZone(zoneId: string | null, mirrorZoneId?: string) {
        setSelectedMirrorZoneId(mirrorZoneId ?? null);
        if (!isDashboard && supportsZoneRoute(location.pathname)) {
            if (zoneId) {
                // Store update deferred to URL sync effect so the zone selector
                // doesn't update before a dirty-form blocker has a chance to cancel
                navigate(buildZoneUrl(location.pathname, zoneId));
            } else {
                globalActions.zones.setSelectedGroup(null);
                globalActions.zones.setDashboardGroup(null);
                navigate(getPageBase(location.pathname));
            }
        } else {
            globalActions.zones.setSelectedGroup(zoneId);
            globalActions.zones.setDashboardGroup(zoneId);
        }
    }

    function handleToggle() {
        if (isDashboard) {
            if (dashboardGroupId === null) {
                globalActions.zones.setDashboardGroup(selectedGroupId);
            } else {
                globalActions.zones.setDashboardGroup(null);
                setSelectedMirrorZoneId(null);
            }
            return;
        }
        if (selectedGroupId === null) {
            const leafIds = getLeafIds(zones);
            const targetId = previousGroupId ?? zones.find((z) => leafIds.has(z.id) && !z.virtual)?.id ?? null;
            if (targetId) {
                navigateToZone(targetId);
            } else if (selectZoneBtnRef.current) {
                setSelectZoneAnchor(selectZoneBtnRef.current);
            }
        } else {
            navigateToZone(null);
        }
    }

    if (!userData.auth) {
        return null;
    }

    const activeGroupId = isDashboard ? dashboardGroupId : selectedGroupId;
    const activeZone = zones.find((z) => z.id === activeGroupId);
    const mirrorZone = selectedMirrorZoneId ? zones.find((z) => z.id === selectedMirrorZoneId) : null;
    const isAllZones = activeGroupId === null;

    return (
        <Paper
            elevation={1}
            sx={{
                position: 'sticky',
                top: (theme) => theme.mixins.toolbar.minHeight,
                zIndex: 1100,
                borderRadius: 0,
                borderBottom: 1,
                borderColor: 'divider'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 0.75
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button variant="outlined" size="small" onClick={handleToggle}>
                        {isAllZones ? i18n('zone_selector_hide_all') : i18n('zone_selector_show_all')}
                    </Button>
                    <Typography variant="h6" component="span" onClick={(e) => setSelectZoneAnchor(e.currentTarget as HTMLElement)} sx={{ cursor: 'pointer' }}>
                        {isAllZones ? (
                            <span style={{ fontWeight: 400 }}>{i18n('zone_selector_showing_all')}</span>
                        ) : (
                            <>
                                <b>Zone: </b>
                                <span style={{ fontWeight: 400 }}>{getActiveZoneLabel(zonesLoaded, activeZone?.name)}</span>
                                {mirrorZone && (
                                    <Typography component="span" sx={{ fontSize: '0.7em', ml: 0.5, fontWeight: 400, verticalAlign: 'middle' }}>
                                        {`source zone of ${mirrorZone.name}`}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button ref={selectZoneBtnRef} variant="outlined" size="small" onClick={(e) => setSelectZoneAnchor(e.currentTarget)}>
                        {i18n('zone_selector_select')}
                    </Button>
                </Box>
            </Box>
            <SelectZonePopover
                anchorEl={selectZoneAnchor}
                onClose={() => setSelectZoneAnchor(null)}
                onSelect={(id, mirrorId) => navigateToZone(id, mirrorId)}
                zones={zones}
                currentZoneId={selectedMirrorZoneId ?? activeGroupId}
            />
        </Paper>
    );
}

export { ZoneSelector };
